// DS: PO63
import React, { useContext, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { RestrictLoggedOutPayload } from '../redux/loggedout/actionCreators';
import Button from '../Button';
import Flex from '@wix/da-ds/pkg/Flex';
import { ReverseTheme } from '@wix/da-react-context/pkg/ThemeContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SocialLoginModuleLoader from '../SocialLogin/redux/ModuleLoader';
import SocialButtonApple from '../SocialLogin/SocialButtonApple';
import SocialButtonGoogle from '../SocialLogin/SocialButtonGoogle';
import SocialButtonFacebook from '../SocialLogin/SocialButtonFacebook';
import {
  BiData,
  ClickJoinBiEvent,
  LoginOnSignupClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

import s from './SisuFunnelBanner.scss';

export interface Props {
  visible?: boolean;
  restrictLoggedOut: (payload: RestrictLoggedOutPayload) => void;
  pageType?: string;
}

const SisuFunnelBanner: React.FC<React.PropsWithChildren<Props>> = ({
  visible,
  restrictLoggedOut,
  pageType,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isJoinBannerSocialFeatureEnabled = useFlag('join_banner_social');
  const onJoinClick = useCallback(() => {
    restrictLoggedOut({
      restrictedActionName: ReferralType.SISU_FUNNEL_BANNER,
    });
  }, [restrictLoggedOut]);
  const hasSocialButtons = !isMobile || isJoinBannerSocialFeatureEnabled;

  return (
    <ReverseTheme>
      <Flex
        className={classnames(
          s['root'],
          visible && s['root-visible'],
          isJoinBannerSocialFeatureEnabled && s['with-mobile-social-buttons']
        )}
        alignItems="center"
        justifyContent="center"
        direction={isMobile ? 'column' : 'row'}
      >
        <span className={s['text']}>
          <Trans
            i18nKey="sisuFunnel.text.join_new_copy"
            components={{
              b: <b />,
              br: <br />,
              privacyLink: (
                <a href={URLS.PRIVACY} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
              ),
            }}
          />
        </span>

        <div className={s['buttons']}>
          <Button
            href={Url.loginLink()}
            view="b14-1"
            className={classnames(s['button'], s['login'])}
            fullWidth={false}
            biData={BiData<LoginOnSignupClickBiEvent>({
              evid: 245,
              referral_info: ReferralType.SISU_FUNNEL_BANNER,
            })}
          >
            {t('sisuFunnel.login')}
          </Button>
          <Button
            onClick={onJoinClick}
            view="b1-1"
            className={s['button']}
            fullWidth={false}
            biData={BiData<ClickJoinBiEvent>({
              evid: 241,
              referral_info: ReferralType.SISU_FUNNEL_BANNER,
            })}
          >
            {t('sisuFunnel.join')}
          </Button>
          {hasSocialButtons && (
            <SocialLoginModuleLoader>
              {(!isJoinBannerSocialFeatureEnabled || !isMobile) && (
                <div className={s['social-separator']}>
                  {t('sisuFunnel.separator')}
                </div>
              )}
              <SocialButtonGoogle
                className={s['social-button']}
                redirectToSisu
                view="icon"
                lazyload
              />
              <SocialButtonApple
                className={s['social-button']}
                view="icon"
                redirectToSisu
              >
                {t('sisuFunnel.button.continue.apple')}
              </SocialButtonApple>
              <SocialButtonFacebook
                className={s['social-button']}
                view="icon"
                redirectToSisu
              >
                {t('sisuFunnel.button.continue.facebook')}
              </SocialButtonFacebook>
            </SocialLoginModuleLoader>
          )}
        </div>
      </Flex>
    </ReverseTheme>
  );
};
SisuFunnelBanner.displayName = 'SisuFunnelBanner';

export default SisuFunnelBanner;
